import React, { ReactNode, useCallback, useMemo } from 'react';
import getUnicodeFlagIcon from 'country-flag-icons/unicode';
import { getCountryCallingCode } from 'libphonenumber-js';

type Props = {
  value: string;
  onChange: (e: any) => void;
  options: [
    {
      value: string;
      label: string;
      divider: boolean;
    }
  ];
};

type MoreProps = {
  iconComponent: ReactNode;
  arrowComponent: ReactNode;
  unicodeFlags: boolean;
};

export default function CountrySelect({ value, onChange, options, ...rest }: Props) {
  const handleChange = useCallback(
    (event) => {
      const { value: val } = event.target;
      onChange(val === 'ZZ' ? undefined : val);
    },
    [onChange]
  );

  return (
    <select {...rest} value={value || 'ZZ'} onChange={handleChange}>
      {options.map(({ value: optionValue, label, divider }) => (
        <option
          key={divider ? '|' : optionValue || 'ZZ'}
          value={divider ? '|' : optionValue || 'ZZ'}
          disabled={!!divider}
          style={divider ? DIVIDER_STYLE : undefined}
        >
          {label}
        </option>
      ))}
    </select>
  );
}

const DIVIDER_STYLE = {
  fontSize: '1px',
  backgroundColor: 'currentColor',
  color: 'inherit',
};

export function CountrySelectWithIcon({
  value,
  options,
  className = '',
  iconComponent: Icon,
  arrowComponent: Arrow = () => (
    <svg height={15} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1008 1008">
      <path d="M875.4 349.4c0 6.6-4 13.2-7.9 17.2L521.2 712.8c-4 4-11.9 7.9-17.2 7.9-6.6 0-13.2-4-17.2-7.9L140.5 366.6c-4-4-7.9-10.6-7.9-17.2 0-5.3 4-13.2 7.9-17.2l37-37c4-4 10.6-7.9 17.2-7.9 5.3 0 13.2 4 17.2 7.9L504 587.3l292.1-292.1c4-4 11.9-7.9 17.2-7.9 6.6 0 13.2 4 17.2 7.9l37 37c3.9 4 7.9 11.9 7.9 17.2z" />
    </svg>
  ),
  unicodeFlags,
  ...rest
}: MoreProps & any) {
  const selectedOption = useMemo(() => getSelectedOption(options, value), [options, value]);

  return (
    <div className="PhoneInputCountry u-ml0 u-pl2">
      <CountrySelect {...rest} value={value} options={options} className={`PhoneInputCountrySelect ${className}`} />

      {/* Either a Unicode flag icon. */}
      {unicodeFlags && value && <div className="PhoneInputCountryIconUnicode">{getUnicodeFlagIcon(value)}</div>}

      {/* Or an SVG flag icon. */}
      {!(unicodeFlags && value) && (
        <Icon
          country={value}
          label={(selectedOption && selectedOption.label) || 'United States'}
          aspectRatio={unicodeFlags ? 1 : undefined}
        />
      )}

      {value ? <span className="u-ml05 u-mr05">+{getCountryCallingCode(value)}</span> : null}

      <Arrow />
    </div>
  );
}

const getSelectedOption = (options, value) => options.filter((op) => !op.divider && op.value === value);
